import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  Card,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Checkbox,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import {
  ArrowBack,
  AdminPanelSettings,
  Add,
  Edit,
  Delete,
  Security,
  ExpandMore,
  CheckBox,
  CheckBoxOutlineBlank,
  Visibility
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getPermissionsByCategory, getAllPageKeys } from '../config/pagePermissions';

interface Admin {
  id: string;
  username: string;
  email: string;
  role: string;
  permissions: string[];
  isActive: boolean;
  createdAt: Date;
  lastLoginAt: Date | null;
  createdBy: string;
}

const AdminManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newAdminForm, setNewAdminForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'admin',
    permissions: [] as string[]
  });

  const [editForm, setEditForm] = useState({
    email: '',
    role: 'admin',
    permissions: [] as string[],
    isActive: true
  });

  const [viewPermissionsDialog, setViewPermissionsDialog] = useState(false);
  const [viewingAdmin, setViewingAdmin] = useState<Admin | null>(null);

  const permissionsByCategory = getPermissionsByCategory();

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('https://api.sobcof.com/api/admins', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setAdmins(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching admins:', error);
      setError('Admin listesi alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post('https://api.sobcof.com/api/admins', newAdminForm, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('Admin başarıyla oluşturuldu');
        setCreateDialogOpen(false);
        setNewAdminForm({
          username: '',
          email: '',
          password: '',
          role: 'admin',
          permissions: []
        });
        fetchAdmins();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Admin oluşturulamadı');
    }
  };

  const handleEditAdmin = (adminData: Admin) => {
    setSelectedAdmin(adminData);
    setEditForm({
      email: adminData.email,
      role: adminData.role,
      permissions: adminData.permissions,
      isActive: adminData.isActive
    });
    setEditDialogOpen(true);
  };

  const handleUpdateAdmin = async () => {
    if (!selectedAdmin) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`https://api.sobcof.com/api/admins/${selectedAdmin.id}`, editForm, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('Admin başarıyla güncellendi');
      setEditDialogOpen(false);
      fetchAdmins();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Admin güncellenemedi');
    }
  };

  const handleDeleteAdmin = (adminData: Admin) => {
    setSelectedAdmin(adminData);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteAdmin = async () => {
    if (!selectedAdmin) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`https://api.sobcof.com/api/admins/${selectedAdmin.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('Admin başarıyla silindi');
      setDeleteDialogOpen(false);
      setSelectedAdmin(null);
      fetchAdmins();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Admin silinemedi');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin': return 'error';
      case 'admin': return 'primary';
      case 'moderator': return 'secondary';
      default: return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin': return 'Süper Admin';
      case 'admin': return 'Admin';
      case 'moderator': return 'Moderatör';
      default: return role;
    }
  };

  // Only super_admin can access this page
  if (admin?.role !== 'super_admin') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error">
          Bu sayfaya erişim yetkiniz yok.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc' }}>
      {/* App Bar */}
      <AppBar 
        position="static" 
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            mr: 2,
            width: 40,
            height: 40
          }}>
            <AdminPanelSettings sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{ 
            flexGrow: 1,
            fontWeight: 600,
            color: 'white'
          }}>
            Admin Yönetimi
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
          >
            Yeni Admin
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Stats Card */}
        <Card elevation={0} sx={{ 
          p: 3, 
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Security sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {admins.length}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Toplam Admin
              </Typography>
            </Box>
          </Box>
        </Card>

        {/* Admins Table */}
        <Card elevation={0} sx={{ 
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          overflow: 'hidden'
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Admin</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Rol</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Sayfa İzinleri</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Son Giriş</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {admins.map((adminData) => (
                  <TableRow 
                    key={adminData.id}
                    sx={{ 
                      '&:hover': { backgroundColor: '#f8fafc' },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ 
                          bgcolor: 'primary.main',
                          width: 35,
                          height: 35
                        }}>
                          <AdminPanelSettings sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {adminData.username}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{adminData.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={getRoleLabel(adminData.role)}
                        color={getRoleColor(adminData.role) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {adminData.role === 'super_admin' ? (
                        <Chip
                          label="Tüm Sayfalar"
                          color="error"
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      ) : adminData.permissions?.includes('all') ? (
                        <Chip
                          label="Tüm Sayfalar"
                          color="primary"
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      ) : (
                        <Tooltip title="İzinleri görüntüle">
                          <Chip
                            label={`${adminData.permissions?.filter(p => !['read', 'write', 'all'].includes(p)).length || 0} Sayfa`}
                            color="default"
                            size="small"
                            onClick={() => {
                              setViewingAdmin(adminData);
                              setViewPermissionsDialog(true);
                            }}
                            sx={{ cursor: 'pointer', fontWeight: 500 }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={adminData.isActive ? 'Aktif' : 'Pasif'}
                        color={adminData.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {adminData.lastLoginAt 
                        ? new Date(adminData.lastLoginAt).toLocaleDateString('tr-TR')
                        : 'Hiç giriş yapmadı'
                      }
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="İzinleri Görüntüle">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setViewingAdmin(adminData);
                              setViewPermissionsDialog(true);
                            }}
                            color="info"
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Düzenle">
                          <IconButton
                            size="small"
                            onClick={() => handleEditAdmin(adminData)}
                            disabled={adminData.username === admin?.username && adminData.role === 'super_admin'}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteAdmin(adminData)}
                            disabled={adminData.username === admin?.username && adminData.role === 'super_admin'}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      {/* Create Admin Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminPanelSettings color="primary" />
            Yeni Admin Oluştur
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Kullanıcı Adı"
                value={newAdminForm.username}
                onChange={(e) => setNewAdminForm({...newAdminForm, username: e.target.value})}
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                value={newAdminForm.email}
                onChange={(e) => setNewAdminForm({...newAdminForm, email: e.target.value})}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Şifre"
                type="password"
                value={newAdminForm.password}
                onChange={(e) => setNewAdminForm({...newAdminForm, password: e.target.value})}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Rol</InputLabel>
                <Select
                  value={newAdminForm.role}
                  onChange={(e) => setNewAdminForm({...newAdminForm, role: e.target.value})}
                >
                  <MenuItem value="super_admin">Süper Admin</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="moderator">Moderatör</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Sayfa İzinleri */}
            {newAdminForm.role !== 'super_admin' && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Security fontSize="small" color="primary" />
                    Sayfa Erişim İzinleri
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CheckBox />}
                      onClick={() => setNewAdminForm({...newAdminForm, permissions: getAllPageKeys()})}
                    >
                      Tümünü Seç
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<CheckBoxOutlineBlank />}
                      onClick={() => setNewAdminForm({...newAdminForm, permissions: []})}
                    >
                      Temizle
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                  {Object.entries(permissionsByCategory).map(([category, pages]) => (
                    <Accordion key={category} defaultExpanded={false} disableGutters elevation={0}>
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          bgcolor: '#f8fafc',
                          '&:hover': { bgcolor: '#f1f5f9' },
                          minHeight: 48
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Checkbox
                            size="small"
                            checked={pages.every(p => newAdminForm.permissions.includes(p.key))}
                            indeterminate={
                              pages.some(p => newAdminForm.permissions.includes(p.key)) &&
                              !pages.every(p => newAdminForm.permissions.includes(p.key))
                            }
                            onChange={(e) => {
                              e.stopPropagation();
                              const pageKeys = pages.map(p => p.key);
                              if (e.target.checked) {
                                const combined = [...newAdminForm.permissions, ...pageKeys];
                                const unique = combined.filter((item, index) => combined.indexOf(item) === index);
                                setNewAdminForm({
                                  ...newAdminForm,
                                  permissions: unique
                                });
                              } else {
                                setNewAdminForm({
                                  ...newAdminForm,
                                  permissions: newAdminForm.permissions.filter(p => !pageKeys.includes(p))
                                });
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {category}
                          </Typography>
                          <Chip
                            label={`${pages.filter(p => newAdminForm.permissions.includes(p.key)).length}/${pages.length}`}
                            size="small"
                            color={pages.every(p => newAdminForm.permissions.includes(p.key)) ? 'success' : 'default'}
                            sx={{ ml: 'auto', mr: 1 }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                        <FormGroup>
                          {pages.map((page) => (
                            <FormControlLabel
                              key={page.key}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={newAdminForm.permissions.includes(page.key)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewAdminForm({
                                        ...newAdminForm,
                                        permissions: [...newAdminForm.permissions, page.key]
                                      });
                                    } else {
                                      setNewAdminForm({
                                        ...newAdminForm,
                                        permissions: newAdminForm.permissions.filter(p => p !== page.key)
                                      });
                                    }
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">{page.label}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {page.description}
                                  </Typography>
                                </Box>
                              }
                              sx={{ ml: 2, mb: 0.5 }}
                            />
                          ))}
                        </FormGroup>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Seçili: {newAdminForm.permissions.length} sayfa
                </Typography>
              </Box>
            )}

            {newAdminForm.role === 'super_admin' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Süper Admin rolü tüm sayfalara erişim yetkisine sahiptir.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e2e8f0', p: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)}>İptal</Button>
          <Button onClick={handleCreateAdmin} variant="contained" startIcon={<Add />}>
            Oluştur
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Admin Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit color="primary" />
            Admin Düzenle: {selectedAdmin?.username}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Rol</InputLabel>
                <Select
                  value={editForm.role}
                  onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                >
                  <MenuItem value="super_admin">Süper Admin</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="moderator">Moderatör</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm({...editForm, isActive: e.target.checked})}
                />
              }
              label="Aktif"
            />

            {/* Sayfa İzinleri */}
            {editForm.role !== 'super_admin' && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Security fontSize="small" color="primary" />
                    Sayfa Erişim İzinleri
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CheckBox />}
                      onClick={() => setEditForm({...editForm, permissions: getAllPageKeys()})}
                    >
                      Tümünü Seç
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<CheckBoxOutlineBlank />}
                      onClick={() => setEditForm({...editForm, permissions: []})}
                    >
                      Temizle
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                  {Object.entries(permissionsByCategory).map(([category, pages]) => (
                    <Accordion key={category} defaultExpanded={false} disableGutters elevation={0}>
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          bgcolor: '#f8fafc',
                          '&:hover': { bgcolor: '#f1f5f9' },
                          minHeight: 48
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Checkbox
                            size="small"
                            checked={pages.every(p => editForm.permissions.includes(p.key))}
                            indeterminate={
                              pages.some(p => editForm.permissions.includes(p.key)) &&
                              !pages.every(p => editForm.permissions.includes(p.key))
                            }
                            onChange={(e) => {
                              e.stopPropagation();
                              const pageKeys = pages.map(p => p.key);
                              if (e.target.checked) {
                                const combined = [...editForm.permissions, ...pageKeys];
                                const unique = combined.filter((item, index) => combined.indexOf(item) === index);
                                setEditForm({
                                  ...editForm,
                                  permissions: unique
                                });
                              } else {
                                setEditForm({
                                  ...editForm,
                                  permissions: editForm.permissions.filter(p => !pageKeys.includes(p))
                                });
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {category}
                          </Typography>
                          <Chip
                            label={`${pages.filter(p => editForm.permissions.includes(p.key)).length}/${pages.length}`}
                            size="small"
                            color={pages.every(p => editForm.permissions.includes(p.key)) ? 'success' : 'default'}
                            sx={{ ml: 'auto', mr: 1 }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                        <FormGroup>
                          {pages.map((page) => (
                            <FormControlLabel
                              key={page.key}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={editForm.permissions.includes(page.key)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setEditForm({
                                        ...editForm,
                                        permissions: [...editForm.permissions, page.key]
                                      });
                                    } else {
                                      setEditForm({
                                        ...editForm,
                                        permissions: editForm.permissions.filter(p => p !== page.key)
                                      });
                                    }
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">{page.label}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {page.description}
                                  </Typography>
                                </Box>
                              }
                              sx={{ ml: 2, mb: 0.5 }}
                            />
                          ))}
                        </FormGroup>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Seçili: {editForm.permissions.length} sayfa
                </Typography>
              </Box>
            )}

            {editForm.role === 'super_admin' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Süper Admin rolü tüm sayfalara erişim yetkisine sahiptir.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e2e8f0', p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)}>İptal</Button>
          <Button onClick={handleUpdateAdmin} variant="contained" startIcon={<Edit />}>
            Güncelle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Admin Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Admin Sil</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{selectedAdmin?.username}</strong> adminini silmek istediğinizden emin misiniz?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={confirmDeleteAdmin} variant="contained" color="error">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Permissions Dialog */}
      <Dialog
        open={viewPermissionsDialog}
        onClose={() => {
          setViewPermissionsDialog(false);
          setViewingAdmin(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility color="info" />
            {viewingAdmin?.username} - Sayfa İzinleri
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {viewingAdmin?.role === 'super_admin' ? (
            <Alert severity="info">
              Süper Admin rolü tüm sayfalara erişim yetkisine sahiptir.
            </Alert>
          ) : viewingAdmin?.permissions?.includes('all') ? (
            <Alert severity="success">
              Bu admin tüm sayfalara erişim yetkisine sahiptir.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Bu admin aşağıdaki sayfalara erişebilir:
              </Typography>

              {Object.entries(permissionsByCategory).map(([category, pages]) => {
                const allowedPages = pages.filter(p => viewingAdmin?.permissions?.includes(p.key));
                if (allowedPages.length === 0) return null;

                return (
                  <Box key={category} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#8B4513' }}>
                      {category}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {allowedPages.map((page) => (
                        <Chip
                          key={page.key}
                          label={page.label}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                );
              })}

              {viewingAdmin?.permissions?.filter(p => !['read', 'write', 'all'].includes(p)).length === 0 && (
                <Alert severity="warning">
                  Bu admin henüz hiçbir sayfaya erişim iznine sahip değil.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e2e8f0', p: 2 }}>
          <Button
            onClick={() => {
              setViewPermissionsDialog(false);
              setViewingAdmin(null);
            }}
          >
            Kapat
          </Button>
          {viewingAdmin?.role !== 'super_admin' && (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => {
                setViewPermissionsDialog(false);
                if (viewingAdmin) {
                  handleEditAdmin(viewingAdmin);
                }
              }}
            >
              Düzenle
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminManagementPage;
